import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePermissions } from "@/hooks/usePermissions";
import { useSubscription } from "@/hooks/useSettings";
import { useEffect, lazy, Suspense } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PwaInstallBanner } from "@/components/PwaInstallBanner";
import { PwaUpdateToast } from "@/components/PwaUpdateToast";
import { BiometricGate } from "@/components/auth/BiometricGate";
import { OnboardingGate } from "@/components/OnboardingGate";

// Lazy-loaded pages for code splitting
const AppLayout = lazy(() => import("@/components/layout/AppLayout").then(m => ({ default: m.AppLayout })));
const MobileLayout = lazy(() => import("@/components/mobile/MobileLayout").then(m => ({ default: m.MobileLayout })));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Trabalhos = lazy(() => import("./pages/Trabalhos"));
const Catalogo = lazy(() => import("./pages/Catalogo"));
const Agenda = lazy(() => import("./pages/Agenda"));
const Clientes = lazy(() => import("./pages/Clientes"));
const Orcamentos = lazy(() => import("./pages/Orcamentos"));
const Faturas = lazy(() => import("./pages/Faturas"));
const Despesas = lazy(() => import("./pages/Despesas"));
const NovaDespesa = lazy(() => import("./pages/NovaDespesa"));
const Fornecedores = lazy(() => import("./pages/Fornecedores"));
const SaudeFinanceira = lazy(() => import("./pages/SaudeFinanceira"));
const Relatorios = lazy(() => import("./pages/Relatorios"));
const Obras = lazy(() => import("./pages/Obras"));
const Definicoes = lazy(() => import("./pages/Definicoes"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

const NotFound = lazy(() => import("./pages/NotFound"));
const PedirOrcamento = lazy(() => import("./pages/PedirOrcamento"));

const SubscricaoConfirmada = lazy(() => import("./pages/SubscricaoConfirmada"));
const AceitarOrcamento = lazy(() => import("./pages/AceitarOrcamento"));
const Manutencao = lazy(() => import("./pages/Manutencao"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const AdminTestInvoiceXpress = lazy(() => import("./pages/AdminTestInvoiceXpress"));
const Onboarding = lazy(() => import("./pages/Onboarding"));

const IS_MAINTENANCE = import.meta.env.VITE_MAINTENANCE_MODE === "true";

// Mobile pages
const MobileHome = lazy(() => import("./pages/mobile/MobileHome"));
const MobileAgenda = lazy(() => import("./pages/mobile/MobileAgenda"));
const MobileTrabalhos = lazy(() => import("./pages/mobile/MobileTrabalhos"));
const MobileOrcamentos = lazy(() => import("./pages/mobile/MobileOrcamentos"));
const MobileClientes = lazy(() => import("./pages/mobile/MobileClientes"));
const MobileDefinicoes = lazy(() => import("./pages/mobile/MobileDefinicoes"));
const MobileJobDetail = lazy(() => import("./pages/mobile/MobileJobDetail"));
const MobileQuoteDetail = lazy(() => import("./pages/mobile/MobileQuoteDetail"));
const MobileClientDetail = lazy(() => import("./pages/mobile/MobileClientDetail"));
const MobileProjectDetail = lazy(() => import("./pages/mobile/MobileProjectDetail"));
const MobileCatalogo = lazy(() => import("./pages/mobile/MobileCatalogo"));
const MobileEventDetail = lazy(() => import("./pages/mobile/MobileEventDetail"));

const queryClient = new QueryClient();

function MobileScrollToTop() {
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  useEffect(() => {
    if (isMobile) {
      window.scrollTo(0, 0);
    }
  }, [pathname, isMobile]);
  return null;
}

const SuspenseFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
  </div>
);

function ProtectedRoute({ children, fallbackTo404 }: { children: React.ReactNode; fallbackTo404?: boolean }) {
  const { user, loading } = useAuth();

  useEffect(() => {
    if (!user?.id) return;

    const bootstrapKey = `gerio-check-subscription:${user.id}`;
    if (sessionStorage.getItem(bootstrapKey)) return;

    sessionStorage.setItem(bootstrapKey, "true");
    supabase.functions.invoke("check-subscription").catch(() => {
      sessionStorage.removeItem(bootstrapKey);
    });
  }, [user?.id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!user) {
    if (fallbackTo404) {
      return <Suspense fallback={<SuspenseFallback />}><NotFound /></Suspense>;
    }
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}


function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const isMobile = useIsMobile();
  const { canAccessFinance, canAccessSettings, canAccessFornecedores } = usePermissions();
  const { data: subscription } = useSubscription();
  const isBusiness = subscription?.plan === "business";
  const isProOrBusiness = subscription?.plan === "pro" || subscription?.plan === "business";

  if (isMobile) {
    return (
      <MobileLayout>
        <Routes>
          <Route path="/" element={<MobileHome />} />
          <Route path="/agenda" element={<MobileAgenda />} />
          <Route path="/agenda/:id" element={<MobileEventDetail />} />
          <Route path="/trabalhos" element={<MobileTrabalhos />} />
          {isProOrBusiness && <Route path="/catalogo" element={<MobileCatalogo />} />}
          <Route path="/trabalhos/:id" element={<MobileJobDetail />} />
          <Route path="/orcamentos" element={<MobileOrcamentos />} />
          <Route path="/orcamentos/:id" element={<MobileQuoteDetail />} />
          <Route path="/clientes" element={<MobileClientes />} />
          <Route path="/clientes/:id" element={<MobileClientDetail />} />
          {canAccessSettings && <Route path="/definicoes" element={<MobileDefinicoes />} />}
          {canAccessFinance && <Route path="/faturas" element={<Faturas />} />}
          {canAccessFinance && <Route path="/despesas" element={<Despesas />} />}
          {canAccessFinance && <Route path="/despesas/nova" element={<NovaDespesa />} />}
          {canAccessFornecedores && <Route path="/fornecedores" element={<Fornecedores />} />}
          {canAccessFinance && <Route path="/saude-financeira" element={<SaudeFinanceira />} />}
          {canAccessFinance && isBusiness && <Route path="/relatorios" element={<Relatorios />} />}
          {!isBusiness && <Route path="/relatorios" element={<Navigate to="/" replace />} />}
          {isBusiness && <Route path="/obras" element={<Obras />} />}
          {isBusiness && <Route path="/obras/:id" element={<MobileProjectDetail />} />}
          {!isBusiness && <Route path="/obras" element={<Navigate to="/" replace />} />}
          {!isBusiness && <Route path="/obras/:id" element={<Navigate to="/" replace />} />}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </MobileLayout>
    );
  }

  return (
    <AppLayout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/trabalhos" element={<Trabalhos />} />
        {isProOrBusiness && <Route path="/catalogo" element={<Catalogo />} />}
        <Route path="/agenda" element={<Agenda />} />
        <Route path="/clientes" element={<Clientes />} />
        <Route path="/orcamentos" element={<Orcamentos />} />
        {canAccessFinance && <Route path="/faturas" element={<Faturas />} />}
        {canAccessFinance && <Route path="/despesas" element={<Despesas />} />}
        {canAccessFinance && <Route path="/despesas/nova" element={<NovaDespesa />} />}
        {canAccessFornecedores && <Route path="/fornecedores" element={<Fornecedores />} />}
        {canAccessFinance && <Route path="/saude-financeira" element={<SaudeFinanceira />} />}
        {canAccessFinance && isBusiness && <Route path="/relatorios" element={<Relatorios />} />}
        {!isBusiness && <Route path="/relatorios" element={<Navigate to="/" replace />} />}
        {isBusiness && <Route path="/obras" element={<Obras />} />}
        {!isBusiness && <Route path="/obras" element={<Navigate to="/" replace />} />}
        
        {canAccessSettings && <Route path="/definicoes" element={<Definicoes />} />}
        <Route path="/admin/test-invoicexpress" element={<AdminTestInvoiceXpress />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AppLayout>
  );
}

const App = () => {
  if (IS_MAINTENANCE) {
    return (
      <ThemeProvider attribute="class" defaultTheme="light" storageKey="gerio-theme">
        <Suspense fallback={<SuspenseFallback />}>
          <Manutencao />
        </Suspense>
      </ThemeProvider>
    );
  }

  return (
  <ThemeProvider attribute="class" defaultTheme="light" storageKey="gerio-theme">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <MobileScrollToTop />
            <AuthProvider>
              <Routes>
                <Route path="/manutencao" element={<Suspense fallback={<SuspenseFallback />}><Manutencao /></Suspense>} />
                <Route path="/login" element={<Suspense fallback={<SuspenseFallback />}><PublicRoute><Auth /></PublicRoute></Suspense>} />
                
                <Route path="/reset-password" element={<Suspense fallback={<SuspenseFallback />}><ResetPassword /></Suspense>} />
                <Route path="/pedir-orcamento/:userId" element={<Suspense fallback={<SuspenseFallback />}><PedirOrcamento /></Suspense>} />
                <Route path="/aceitar-orcamento/:token" element={<Suspense fallback={<SuspenseFallback />}><AceitarOrcamento /></Suspense>} />
                <Route path="/unsubscribe" element={<Suspense fallback={<SuspenseFallback />}><Unsubscribe /></Suspense>} />
                <Route path="/subscricao-confirmada" element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <ProtectedRoute>
                      <SubscricaoConfirmada />
                    </ProtectedRoute>
                  </Suspense>
                } />
                <Route path="/onboarding" element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <ProtectedRoute>
                      <Onboarding />
                    </ProtectedRoute>
                  </Suspense>
                } />
                <Route path="*" element={
                  <Suspense fallback={<SuspenseFallback />}>
                    <ProtectedRoute>
                      <BiometricGate>
                        <OnboardingGate>
                          <CurrencyProvider>
                            <AppRoutes />
                          </CurrencyProvider>
                        </OnboardingGate>
                      </BiometricGate>
                    </ProtectedRoute>
                  </Suspense>
                } />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
      </TooltipProvider>
      <PwaInstallBanner />
      <PwaUpdateToast />
    </QueryClientProvider>
  </ThemeProvider>
  );
};

export default App;
